import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import companyApi from '@data/api/company';
import { Company } from '@src/@data/types/company';
import { useAuth } from '@data/store/AuthProvider';
import { NotImplemented } from '@src/@data/utility/NotImplemented';
import { logOnError } from '@src/@data/utility/errors';
import { AppSpinner } from '@src/components/atoms/AppSpinner';

interface ContextValueType {
  companies: Company[] | undefined;
  updateCompanies: () => Promise<Company[]>;
  isLoadingCompanies: () => boolean;
}

export const CompaniesContext = createContext<ContextValueType>({
  companies: undefined,
  updateCompanies: NotImplemented,
  isLoadingCompanies: NotImplemented,
});

export const useCompanies = () => useContext(CompaniesContext);

export const CompaniesProvider: React.FC = ({ children }) => {
  const { user } = useAuth();

  const [companies, setCompanies] = useState<Company[]>();
  const [isLoading, setIsLoading] = useState(true);

  const saveCompanies = (companies: Company[]) => {
    setCompanies(companies);
  };

  const clearCompanies = () => {
    setCompanies(undefined);
  };

  const updateCompanies = useCallback(() => {
    return companyApi
      .listMyCompanies()
      .then((companies) => {
        saveCompanies(companies);
        return companies;
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const isLoadingCompanies = () => {
    return isLoading;
  };

  useEffect(() => {
    if (user) {
      updateCompanies().catch(logOnError);
    } else {
      clearCompanies();
    }
  }, [updateCompanies, user]);

  return (
    <CompaniesContext.Provider
      value={{
        companies,
        updateCompanies,
        isLoadingCompanies,
      }}
    >
      {!user || !!companies ? children : <AppSpinner />}
    </CompaniesContext.Provider>
  );
};
